export const smartCaptchaClientKey = document
  .querySelector('meta[name="smartcaptcha:client_key"]')
  ?.getAttribute("content");

export const smartCaptchaEnabled = document
  .querySelector('meta[name="smartcaptcha:enabled"]')
  ?.getAttribute("content") !== "false";

export const CAPTCHA_ERROR_MESSAGE =
  "Не удалось пройти проверку на робота. Попробуйте ещё раз.";

const SCRIPT_TIMEOUT = 10000;
const CHALLENGE_TIMEOUT = 120000;
let scriptPromise;

export function initSmartCaptcha() {
  if (window.smartCaptcha) return Promise.resolve(window.smartCaptcha);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    let finished = false;
    const onload = () => {
      if (!window.smartCaptcha) {
        finish(new Error(CAPTCHA_ERROR_MESSAGE));
        return;
      }
      finish(null, window.smartCaptcha);
    };
    const timeout = setTimeout(() => finish(new Error(CAPTCHA_ERROR_MESSAGE)), SCRIPT_TIMEOUT);

    function finish(error, captcha) {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      script.onerror = null;
      script.onload = null;
      if (window.smartCaptchaOnload === onload) delete window.smartCaptchaOnload;
      if (error) {
        script.remove();
        reject(error);
      } else {
        resolve(captcha);
      }
    }

    window.smartCaptchaOnload = onload;
    script.src =
      "https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=smartCaptchaOnload";
    script.defer = true;
    script.onerror = () => finish(new Error(CAPTCHA_ERROR_MESSAGE));
    script.onload = () => {
      if (window.smartCaptcha) finish(null, window.smartCaptcha);
    };
    try {
      document.body.appendChild(script);
    } catch {
      finish(new Error(CAPTCHA_ERROR_MESSAGE));
    }
  }).catch((error) => {
    scriptPromise = undefined;
    throw error;
  });

  return scriptPromise;
}

export default async function executeSmartCaptcha({signal} = {}) {
  signal?.throwIfAborted();
  if (!smartCaptchaEnabled) return "";
  if (!smartCaptchaClientKey) throw new Error(CAPTCHA_ERROR_MESSAGE);

  const captcha = await initSmartCaptcha();
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const container = document.createElement("div");
    container.className = "smart-captcha-container";
    document.body.appendChild(container);

    let widgetId;
    let finished = false;
    const unsubscribe = [];
    let hiddenTimeout;
    const timeout = setTimeout(() => finish(new Error(CAPTCHA_ERROR_MESSAGE)), CHALLENGE_TIMEOUT);
    const onAbort = () => finish(signal.reason);
    signal?.addEventListener("abort", onAbort, {once: true});

    function finish(error, token) {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      clearTimeout(hiddenTimeout);
      signal?.removeEventListener("abort", onAbort);
      unsubscribe.forEach((stop) => {
        try {
          if (typeof stop === "function") stop();
        } catch {
          // Ошибка очистки не должна оставлять форму в состоянии загрузки.
        }
      });
      try {
        if (widgetId !== undefined) captcha.destroy(widgetId);
      } catch {
        // Виджет мог уже удалить себя при завершении проверки.
      }
      container.remove();
      if (error) reject(error);
      else resolve(token);
    }

    try {
      widgetId = captcha.render(container, {
        sitekey: smartCaptchaClientKey,
        invisible: true,
        callback: (token) => {
          if (token) finish(null, token);
          else finish(new Error(CAPTCHA_ERROR_MESSAGE));
        },
        hideShield: true,
      });
      if (finished) {
        captcha.destroy(widgetId);
        return;
      }
      for (const event of ["network-error", "javascript-error", "token-expired"]) {
        unsubscribe.push(
          captcha.subscribe(widgetId, event, () => finish(new Error(CAPTCHA_ERROR_MESSAGE))),
        );
      }
      unsubscribe.push(captcha.subscribe(widgetId, "challenge-hidden", () => {
        hiddenTimeout = setTimeout(() => {
          try {
            const token = captcha.getResponse(widgetId);
            if (token) finish(null, token);
            else finish(new Error(CAPTCHA_ERROR_MESSAGE));
          } catch {
            finish(new Error(CAPTCHA_ERROR_MESSAGE));
          }
        }, 1000);
      }));
      captcha.execute(widgetId);
    } catch {
      finish(new Error(CAPTCHA_ERROR_MESSAGE));
    }
  });
}
